import { ButtonType,ButtonWidget } from '@tableau/widgets';
import React from 'react';

import {
  ActionMenuItemClickedChildFunction,
  ActionMenuItemClickedTelemetryContext,
  CardPositionContext,
  ContentClickedChildFunction,
  ContentClickedTelemetryContext,
  ContentInfoContext,
  FavoriteToggleClickedChildFunction,
  FavoriteToggleClickedTelemetryContext,
  RelatedContentInfoContext,
} from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { Messages } from '../../codegen/Localize';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { LoadMoreButtonClickedEvent, TelemetryEvents } from '../../commonInfra/telemetryEvents/TelemetryEvents';
import {
  ContentLocation,
  FavoriteToggleType,
} from '../../commonInfra/telemetryEvents/types/ContentTelemetryTypes';
import { generateTelemetryEventHandler } from '../../commonInfra/telemetryEvents/util/ContentTelemetry';
import { ShowMoreCardsAction } from '../actions/ShowMoreCardsAction';
import { ChannelStyles, TextChannelStyles } from '../styles/ChannelStyles';
import { HomeStyles } from '../styles/HomeStyles';
import { ChannelTypes } from '../types/ChannelTypes';
import { SupportedContentTypes } from '../types/HomeTypes';
import { ChannelHeader } from './ChannelHeader';

export interface ChannelBaseProps {
  readonly channelType: ChannelTypes;
  readonly title: string;
  readonly description?: string;
  readonly list: ReadonlyArray<SupportedContentTypes>;
  readonly seeAllUrl: string;
  readonly seeAllAltText: string;
  readonly showLastAccessedAt?: boolean;
  readonly isFetching: boolean;
  readonly maxCardsPerRow: number;
  readonly numRows: number;
  readonly spaceInfo: SpaceInfo;
  readonly numCards?: number;
  readonly headerIconPath?: string;
  readonly headerIconNode?: React.ReactNode;
  readonly shouldListWrapVertically?: boolean;
  readonly showMoreCards?: (numCardsToShow: number, channelType: ChannelTypes) => ShowMoreCardsAction;
}

export type ChannelProps = ChannelBaseProps;

export class Channel extends React.PureComponent<ChannelProps> {

  componentDidMount() {
    // If the showMoreCards action has been passed, need to dispatch an initial show more action to
    // set the initial show more state so that the cardsContainer flexes properly on browser resize
    if (this.props.numCards === 0 && this.props.showMoreCards) {
      this.props.showMoreCards(this.props.maxCardsPerRow, this.props.channelType);
    }
  }

  sendLoadMoreClickedTelemetryEvent = (): void => {
    Telemetry.sendEvent<LoadMoreButtonClickedEvent>({
      channelType: this.props.channelType,
      type: TelemetryEvents.LoadMoreButtonClicked,
    });
  };

  handleClickShowMoreButton = (e: React.SyntheticEvent<HTMLDivElement, Event>): void => {
    const numCardsToFillGrid = this.props.numRows * this.props.maxCardsPerRow - this.props.numCards;
    const numCardsToShow: number = this.props.numCards + this.props.maxCardsPerRow + numCardsToFillGrid;
    this.sendLoadMoreClickedTelemetryEvent();
    this.props.showMoreCards(numCardsToShow, this.props.channelType);
  };

  render() {
    return (
      !this.props.isFetching && this.props.list.length > 0  &&
      <ContentClickedTelemetryContext.Provider value={generateContentClickedTelemetryContext(this.props.spaceInfo, this.props.channelType, this.props.list)}>
        <FavoriteToggleClickedTelemetryContext.Provider value={generateFavoriteToggleClickedTelemetryContext(this.props.spaceInfo, this.props.channelType, this.props.list)}>
          <ActionMenuItemClickedTelemetryContext.Provider value={generateActionMenuItemClickedTelemetryContext(this.props.spaceInfo, this.props.channelType, this.props.list)}>
            <section className={HomeStyles.contentSection} data-tb-test-id={`${this.props.channelType}-channel`}>
              <ChannelHeader
                headerIconPath={this.props.headerIconPath}
                headerIconNode={this.props.headerIconNode}
                channelType={this.props.channelType}
                title={this.props.title}
                seeAllUrl={this.props.seeAllUrl}
                seeAllAltText={this.props.seeAllAltText}
              />
              {this.props.description && <p data-tb-test-id={`${this.props.channelType}-channel-description`} className={TextChannelStyles.text}> {this.props.description} </p>}
              <ul className={ChannelStyles.cardsContainer} style={{maxHeight: this.props.numRows * ChannelStyles.cardHeight}}>
                {this.props.children}
              </ul>
              {this.props.numCards && this.props.showMoreCards &&
                <div className={ChannelStyles.showMoreButtonContainer}>
                  <ButtonWidget
                    disabled={this.props.numCards >= this.props.list.length}
                    buttonType={ButtonType.Outline}
                    handleClick={this.handleClickShowMoreButton}
                    testId='show-more-button'
                  >
                    {Messages.channelShowMoreButton()}
                  </ButtonWidget>
                </div>}
            </section>
          </ActionMenuItemClickedTelemetryContext.Provider>
        </FavoriteToggleClickedTelemetryContext.Provider>
      </ContentClickedTelemetryContext.Provider>
    );
  }

}

// Telemetry Handler context values

export function generateContentClickedTelemetryContext(spaceInfo: SpaceInfo, channelType: ChannelTypes, allContents: ReadonlyArray<SupportedContentTypes>) {
  return function ContentClickedTelemetryContext(childFunction: ContentClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <RelatedContentInfoContext.Consumer>
            {relatedContentInfo =>
              <CardPositionContext.Consumer>
                {cardIndex =>
                  childFunction(() => {
                    const eventHandler = generateTelemetryEventHandler(
                      TelemetryEvents.ContentClicked,
                      spaceInfo,
                      allContents,
                      contentInfo,
                      ContentLocation.Home,
                      cardIndex,
                      {
                        homeChannel: channelType,
                        relatedContentId: relatedContentInfo && relatedContentInfo.relatedContentId,
                        // Known bug on TS prevents using the commented line and we have to use an if statemnte
                        // https://github.com/microsoft/TypeScript/issues/32455
                        // relatedContentType: relatedContentInfo && relatedContentInfo.relatedContentType,
                        relatedContentType: relatedContentInfo? relatedContentInfo.relatedContentType : null
                      }
                    );
                    eventHandler();
                  })
                }
              </CardPositionContext.Consumer>
            }
          </RelatedContentInfoContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}

export function generateFavoriteToggleClickedTelemetryContext(spaceInfo: SpaceInfo, channelType: ChannelTypes, allContents: ReadonlyArray<SupportedContentTypes>) {
  return function FavoriteToggleClickedTelemetryContext(childFunction: FavoriteToggleClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <CardPositionContext.Consumer>
            {cardIndex =>
              childFunction((toggleType: FavoriteToggleType) => {
                const eventHandler = generateTelemetryEventHandler(
                  TelemetryEvents.FavoriteToggleClicked,
                  spaceInfo,
                  allContents,
                  contentInfo,
                  ContentLocation.Home,
                  cardIndex,
                  {homeChannel: channelType}
                );
                eventHandler(toggleType);
              })
            }
          </CardPositionContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}

export function generateActionMenuItemClickedTelemetryContext(spaceInfo: SpaceInfo, channelType: ChannelTypes, allContents: ReadonlyArray<SupportedContentTypes>) {
  return function ActionMenuItemClickedTelemetryContext(childFunction: ActionMenuItemClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <CardPositionContext.Consumer>
            {cardIndex =>
              childFunction((actionType: string) => {
                const eventHandler = generateTelemetryEventHandler(
                  TelemetryEvents.ActionMenuItemClicked,
                  spaceInfo,
                  allContents,
                  contentInfo,
                  ContentLocation.Home,
                  cardIndex,
                  {homeChannel: channelType}
                );
                eventHandler(actionType);
              })
            }
          </CardPositionContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}
